/**
 * LinkCell Component
 *
 * Renders a URL with optional display text.
 * Supports inline editing with URL validation.
 */

import { useState, useRef, useEffect, useCallback, Fragment } from 'react';
import { Transition } from '@headlessui/react';
import { LinkIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import type { LinkCellProps } from './types';

const URL_REGEX = /^(https?:\/\/)?([\w-]+\.)+[\w-]+(\/[\w-./?%&=]*)?$/i;

export function LinkCell({
  value,
  onChange,
  readonly = false,
}: LinkCellProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editUrl, setEditUrl] = useState(value?.url ?? '');
  const [editText, setEditText] = useState(value?.text ?? '');
  const [error, setError] = useState('');
  const urlInputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const validateUrl = useCallback((url: string): boolean => {
    if (!url) return true;
    return URL_REGEX.test(url);
  }, []);

  const normalizeUrl = useCallback((url: string): string => {
    if (!url) return '';
    if (!/^https?:\/\//i.test(url)) {
      return `https://${url}`;
    }
    return url;
  }, []);

  const handleSubmit = useCallback(() => {
    setIsEditing(false);
    if (!onChange) return;

    const trimmedUrl = editUrl.trim();
    const trimmedText = editText.trim();

    if (!trimmedUrl) {
      onChange(null);
      setError('');
      return;
    }

    if (!validateUrl(trimmedUrl)) {
      setError('Invalid URL format');
      return;
    }

    setError('');
    onChange({
      url: normalizeUrl(trimmedUrl),
      text: trimmedText || undefined,
    });
  }, [editUrl, editText, onChange, validateUrl, normalizeUrl]);

  useEffect(() => {
    if (isEditing && urlInputRef.current) {
      urlInputRef.current.focus();
    }
  }, [isEditing]);

  useEffect(() => {
    setEditUrl(value?.url ?? '');
    setEditText(value?.text ?? '');
  }, [value?.url, value?.text]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        handleSubmit();
      }
    };

    if (isEditing) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isEditing, handleSubmit]);

  const handleClear = () => {
    if (!onChange) return;
    onChange(null);
    setEditUrl('');
    setEditText('');
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
    if (e.key === 'Escape') {
      setIsEditing(false);
      setEditUrl(value?.url ?? '');
      setEditText(value?.text ?? '');
      setError('');
    }
  };

  if (readonly) {
    return (
      <div className="w-full h-full flex items-center overflow-hidden">
        {value?.url ? (
          <a
            href={value.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-primary-600 hover:text-primary-700 hover:underline truncate flex items-center gap-1"
          >
            {value.text || value.url}
            <ArrowTopRightOnSquareIcon className="h-3 w-3 flex-shrink-0" />
          </a>
        ) : (
          <span className="text-gray-400 text-sm">-</span>
        )}
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative w-full">
      {!isEditing ? (
        <div
          onClick={() => setIsEditing(true)}
          className="w-full h-full flex items-center cursor-text min-h-[28px] overflow-hidden"
        >
          {value?.url ? (
            <div className="flex items-center gap-1 overflow-hidden">
              <LinkIcon className="h-3.5 w-3.5 text-primary-500 flex-shrink-0" />
              <span className="text-sm text-primary-600 truncate">
                {value.text || value.url}
              </span>
            </div>
          ) : (
            <span className="text-gray-400 text-sm hover:text-gray-500">-</span>
          )}
        </div>
      ) : (
        <Transition
          show={isEditing}
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
        >
          <div className="absolute z-50 top-0 left-0 bg-white rounded-lg shadow-dropdown border border-surface-border p-3 w-72">
            <div className="space-y-3">
              {/* URL input */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  URL
                </label>
                <input
                  ref={urlInputRef}
                  type="text"
                  value={editUrl}
                  onChange={(e) => {
                    setEditUrl(e.target.value);
                    setError('');
                  }}
                  onKeyDown={handleKeyDown}
                  placeholder="https://example.com"
                  className={`
                    w-full text-sm border rounded px-2 py-1.5
                    focus:outline-none focus:ring-1
                    ${error
                      ? 'border-red-300 focus:ring-red-500'
                      : 'border-surface-border focus:ring-primary-500'
                    }
                  `}
                />
                {error && (
                  <p className="mt-1 text-xs text-red-500">{error}</p>
                )}
              </div>

              {/* Display text input */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Display text (optional)
                </label>
                <input
                  type="text"
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Link text"
                  className="w-full text-sm border border-surface-border rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
              </div>

              {/* Actions */}
              <div className="flex justify-between pt-2 border-t border-surface-border">
                <button
                  onClick={handleClear}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  Clear
                </button>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setIsEditing(false);
                      setEditUrl(value?.url ?? '');
                      setEditText(value?.text ?? '');
                      setError('');
                    }}
                    className="text-sm text-gray-500 hover:text-gray-700"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSubmit}
                    className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          </div>
        </Transition>
      )}
    </div>
  );
}
